/**
 * Cockpit principal : Score Culture Sécurité, Alertes, KPIs (~25 cartes) et
 * l'ensemble des graphiques (Radar, Camembert, Histogramme, Évolution,
 * Top Risques, Classement, Heatmap).
 */
import { useEffect, useState } from "react";
import {
  Activity, AlertOctagon, AlertTriangle, CheckCircle2, ClipboardCheck, Eye,
  FileWarning, Flame, Gauge, HeartPulse, RefreshCw, ShieldCheck, Target, Timer, TrendingUp,
} from "lucide-react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import KpiCard from "@/components/kpi/KpiCard";
import BogScoreCard from "@/components/kpi/BogScoreCard";
import AlertsPanel from "@/components/alerts/AlertsPanel";
import RadarPerformanceChart from "@/components/charts/RadarPerformanceChart";
import FieldActivityChart from "@/components/charts/FieldActivityChart";
import FindingsPieChart from "@/components/charts/FindingsPieChart";
import MonthlyEvolutionChart from "@/components/charts/MonthlyEvolutionChart";
import TopRisksChart from "@/components/charts/TopRisksChart";
import SupervisorRankingTable from "@/components/charts/SupervisorRankingTable";
import MultiSiteHeatmap from "@/components/charts/MultiSiteHeatmap";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DASHBOARD } from "@/constants/testIds";

export default function Dashboard() {
  const { filters, refreshKey } = useFilters();
  const [dashboard, setDashboard] = useState(null);
  const [bogScore, setBogScore] = useState(null);
  const [topRisks, setTopRisks] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      dashboardApi.getDashboard(filters),
      dashboardApi.getBogScore2026(filters),
      dashboardApi.getTopRisques(filters),
      dashboardApi.getRanking(filters),
      dashboardApi.getHeatmap(filters),
    ])
      .then(([d, b, t, r, h]) => {
        setDashboard(d.data);
        setBogScore(b.data);
        setTopRisks(t.data.items);
        setRanking(r.data.items);
        setHeatmap(h.data);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [filters, refreshKey, retryTick]);

  if (error && !dashboard) {
    return (
      <div data-testid="dashboard-error" className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white py-16 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
        <p className="text-sm text-slate-600">Le chargement des données a échoué (connexion lente ou interrompue).</p>
        <Button data-testid="dashboard-retry-button" variant="outline" className="gap-2" onClick={() => setRetryTick((t) => t + 1)}>
          <RefreshCw className="h-4 w-4" /> Réessayer
        </Button>
      </div>
    );
  }

  if (loading || !dashboard) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-lg" />
        ))}
      </div>
    );
  }

  const { kpis, alerts, charts } = dashboard;

  const kpiCards = [
    { label: "TIF Mois", value: kpis.tif_month, suffix: "h", icon: Gauge, color: "blue" },
    { label: "TIF Année", value: kpis.tif_year, suffix: "h", icon: Gauge, color: "blue" },
    { label: "VPC Mois", value: kpis.vpc_month, icon: Eye, color: "green" },
    { label: "VPC Année", value: kpis.vpc_year, icon: Eye, color: "green" },
    { label: "cVPC Mois", value: kpis.cvpc_month, icon: Eye, color: "green" },
    { label: "cVPC Année", value: kpis.cvpc_year, icon: Eye, color: "green" },
    { label: "HazID Mois", value: kpis.hazid_month, icon: AlertOctagon, color: "orange" },
    { label: "HazID Année", value: kpis.hazid_year, icon: AlertOctagon, color: "orange" },
    { label: "SHIELD Mois", value: kpis.shield_month, icon: ShieldCheck, color: "blue" },
    { label: "SHIELD Année", value: kpis.shield_year, icon: ShieldCheck, color: "blue" },
    { label: "Findings High", value: kpis.findings_high, icon: Flame, color: "red" },
    { label: "Findings Medium", value: kpis.findings_medium, icon: AlertTriangle, color: "orange" },
    { label: "Findings Low", value: kpis.findings_low, icon: FileWarning, color: "slate" },
    { label: "Findings Ouverts", value: kpis.findings_open, icon: Activity, color: "blue" },
    { label: "Findings en Retard", value: kpis.findings_late, icon: Timer, color: "red" },
    { label: "Findings CIS France", value: kpis.findings_cis_france, icon: AlertOctagon, color: "red" },
    { label: "Actions Icare en Retard", value: kpis.actions_late, icon: ClipboardCheck, color: "red" },
    { label: "CCV Ouvertes", value: kpis.ccv_open, icon: CheckCircle2, color: "blue" },
    { label: "CCV en Retard", value: kpis.ccv_late, icon: Timer, color: "orange" },
    { label: "ARED Disponibles", value: kpis.ared_available, icon: HeartPulse, color: "green" },
    { label: "PCED Disponibles", value: kpis.pced_available, subtext: `dont ${kpis.pced_validated} PCED validées`, icon: ShieldCheck, color: "green" },
    { label: "Objectif ARED", value: kpis.objectif_ared, icon: Target, color: "slate" },
    { label: "Objectif PCED", value: kpis.objectif_pced, icon: Target, color: "slate" },
    { label: "Progression", value: kpis.progression_pct, suffix: "%", icon: TrendingUp, color: "blue" },
  ];

  return (
    <div data-testid={DASHBOARD.container} className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <BogScoreCard bogScore={bogScore} />
        </div>
        <div className="lg:col-span-3">
          <AlertsPanel alerts={alerts} />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">Indicateurs Clés</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {kpiCards.map((kpi) => (
            <KpiCard key={kpi.label} testId={`kpi-card-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`} {...kpi} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <RadarPerformanceChart data={charts.radar} />
        <FindingsPieChart data={charts.findings_pie} />
        <FieldActivityChart data={charts.field_activity} />
        <MonthlyEvolutionChart data={charts.monthly_evolution} />
        <TopRisksChart data={topRisks} />
        <SupervisorRankingTable data={ranking} />
      </div>

      {heatmap && <MultiSiteHeatmap sites={heatmap.sites} months={heatmap.months} matrix={heatmap.matrix} />}
    </div>
  );
}
